import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, Printer, Receipt as ReceiptIcon } from 'lucide-react';
import { fmtDate, naira } from '@/lib/format';
import { useSchool } from '@/lib/useSchool';
import { getPayments, getPaymentsById, getReceiptsById } from '@/api/parent';
import { ErrorBox, Loading, Pager, fmtDateTime, paged, unwrap, PageTitle } from '../ui';
import { Sheet, ghost, primary } from '../wallet/ui';

interface PaymentRow { id: number; reference: string; method: string; amount: number; receipt_no: string | null; student: string; paid_at: string | null }
interface PaymentDetail extends PaymentRow { status: string; receipt_id: number | null; allocations: { invoice_no: string | null; amount: number }[] }
interface ReceiptData { number: string; issued_at: string; student: string; amount: number; method: string; void: boolean; lines: { invoice_no: string | null; amount: number }[] }

const METHOD: Record<string, string> = { wallet_transfer: 'Wallet', bank_transfer: 'Bank transfer', cash: 'Cash', pos: 'POS', card: 'Card', ussd: 'USSD', paystack: 'Paystack' };
const method = (m: string) => METHOD[m] ?? m.replace(/_/g, ' ');

/** One payment: what it paid for, and its receipt (printable). */
function PaymentSheet({ id, onClose }: { id: number; onClose: () => void }) {
  const school = useSchool();
  const q = useQuery({ queryKey: ['parent-payments', 'detail', id], queryFn: () => unwrap<PaymentDetail>(getPaymentsById(id)) });
  const p = q.data;
  const r = useQuery({ queryKey: ['parent-receipt', p?.receipt_id], enabled: !!p?.receipt_id, queryFn: () => unwrap<ReceiptData>(getReceiptsById(p!.receipt_id!)) });
  const rc = r.data;
  return (
    <Sheet title={rc ? `Receipt ${rc.number}` : 'Payment'} onClose={onClose}
      footer={<><button onClick={onClose} className={ghost}>Close</button>{rc && <button onClick={() => window.print()} className={primary}><Printer className="w-4 h-4 inline -mt-0.5 mr-1" />Print / save PDF</button>}</>}>
      {q.isLoading ? <Loading /> : q.isError || !p ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : (
        <div className="print-area space-y-4 text-sm">
          <div className="text-center">
            {school.logo && <img src={school.logo} alt="" className="w-12 h-12 object-contain mx-auto mb-1" />}
            <p className="font-bold text-slate-900">{school.name}</p>
            <p className="text-xs text-slate-500">Official payment receipt</p>
          </div>
          <div className="text-center">
            <CheckCircle2 className={`w-10 h-10 mx-auto ${rc?.void ? 'text-red-500' : 'text-green-600'}`} />
            <p className="text-3xl font-extrabold mt-1">{naira(p.amount)}</p>
            <p className="text-xs text-slate-500">{rc?.void ? 'This payment was reversed' : `Paid ${p.paid_at ? fmtDateTime(p.paid_at) : ''}`}</p>
          </div>
          <dl className="rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {[['Student', p.student], ['Paid by', method(p.method)], ['Reference', p.reference], ['Receipt no.', rc?.number ?? p.receipt_no ?? '—']].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 px-3 py-2"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-right break-all">{v}</dd></div>
            ))}
          </dl>
          {p.allocations.length > 0 && (
            <div>
              <p className="font-semibold mb-1">Paid towards</p>
              <ul className="rounded-2xl border border-slate-200 divide-y divide-slate-100">
                {p.allocations.map((a, i) => <li key={i} className="flex justify-between px-3 py-2"><span>{a.invoice_no ?? 'Invoice'}</span><span className="font-semibold">{naira(a.amount)}</span></li>)}
              </ul>
            </div>
          )}
          {!p.receipt_id && <p className="text-xs text-slate-500">The receipt is still being prepared.</p>}
        </div>
      )}
    </Sheet>
  );
}

export function PaymentHistoryPage() {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['parent-payments', page], queryFn: () => paged<PaymentRow>(getPayments({ page })) });
  const rows = q.data?.data ?? [];
  const total = rows.reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div className="space-y-4 max-w-3xl">
      <PageTitle kicker="Payments" title="Payments & receipts" sub="Every school payment for your children. Tap one to see or print its receipt." />
      <section className="bg-white rounded-2xl border border-slate-200">
        {q.isLoading ? <p className="p-4"><Loading /></p> : q.isError ? <div className="p-4"><ErrorBox error={q.error} onRetry={() => q.refetch()} /></div> : !rows.length ? (
          <div className="p-8 text-center"><ReceiptIcon className="w-8 h-8 text-slate-300 mx-auto" /><p className="text-sm text-slate-500 mt-2">No payments yet. When you pay fees, the receipt appears here.</p></div>
        ) : (
          <>
            <p className="px-4 pt-3 text-xs text-slate-500">{q.data?.meta?.total ?? rows.length} payment(s){page === 1 ? ` · ${naira(total)} on this page` : ''}</p>
            <ul className="divide-y divide-slate-100">
              {rows.map((p) => (
                <li key={p.id}>
                  <button onClick={() => setOpen(p.id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                    <span className="w-9 h-9 rounded-full bg-green-50 text-green-700 grid place-items-center shrink-0"><ReceiptIcon className="w-4 h-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{p.student}</span>
                      <span className="block text-xs text-slate-500 truncate">{p.paid_at ? fmtDate(p.paid_at) : '—'} · {method(p.method)}{p.receipt_no ? ` · ${p.receipt_no}` : ''}</span>
                    </span>
                    <span className="text-sm font-bold shrink-0">{naira(p.amount)}</span>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-4 pb-3"><Pager meta={q.data?.meta} onPage={setPage} /></div>
          </>
        )}
      </section>
      {open && <PaymentSheet id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
